import { useRef } from 'react'

const HOLD_MS = 450
const SLOP_PX = 10

/**
 * Tap fires immediately; a hold opens the sheet. The tap must stay instant —
 * it happens dozens of times a day and is the whole tactile identity of the app
 * — so the hold is detected without delaying the tap.
 */
export function useLongPress(onLongPress: () => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const origin = useRef<{ x: number; y: number } | null>(null)
  const fired = useRef(false)

  function clear() {
    if (timer.current) clearTimeout(timer.current)
    timer.current = undefined
    origin.current = null
  }

  return {
    /** True when the last gesture was a hold, so the click can be swallowed. */
    consumed: () => {
      const value = fired.current
      fired.current = false
      return value
    },
    handlers: {
      onPointerDown(event: React.PointerEvent) {
        fired.current = false
        origin.current = { x: event.clientX, y: event.clientY }
        timer.current = setTimeout(() => {
          fired.current = true
          onLongPress()
        }, HOLD_MS)
      },
      onPointerMove(event: React.PointerEvent) {
        const start = origin.current
        if (!start) return
        if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > SLOP_PX) clear()
      },
      onPointerUp: clear,
      onPointerCancel: clear,
      onContextMenu(event: React.MouseEvent) {
        // A long press on Android raises the context menu; the sheet is the point.
        if (fired.current) event.preventDefault()
      },
    },
  }
}
